import { useState, forwardRef, useMemo, useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'
import {
  cn,
  SelectCheckboxItem,
  Input,
  SelectActions,
  SelectFooter,
  FormSelectTrigger,
  FormSelect,
  FormSelectContent,
  FormSelectGroup,
} from '@wellesley/ui'
import { useCurrentUser } from '@wellesley/user'
import useDebounceValue from 'hooks/useDebounceValue'
import {
  User,
  EventAttendee,
  useGetApiV1SearchQuery,
  usePostApiV1EventsByEventIdAttendeesAndUserIdMutation,
  useDeleteApiV1EventsByEventIdAttendeesAndUserIdMutation,
} from '@wellesley/openapi'
import { EventUser } from '../EventUser'
import AttendeesList from './AttendeesList'
import { EventResolvedAttendee } from '../../helpers/Event.types'
import { useGetApiV1EventsByEventIdAttendeesQuery } from '../../api'

type AttendeesFieldProps = Partial<
  Omit<ControllerRenderProps<FieldValues, string>, 'value' | 'onChange' | 'ref'>
> & {
  readOnly?: boolean
  isEdit?: boolean
  eventId: string | undefined
  onChange?: (value: string[]) => void
  placeholder?: string
  className?: string
  hasAttendees?: boolean
}

const listClasses = '!h-auto min-h-xxxl w-full [&>div]:max-w-full'

export const AttendeesField = forwardRef<
  HTMLButtonElement,
  AttendeesFieldProps
>(
  (
    { readOnly, isEdit, eventId, onChange, className, hasAttendees, ...rest },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [searchText, setSearchText] = useState('')
    const debouncedSearchText = useDebounceValue(searchText, 300)

    const [attendeesState, setAttendeesState] = useState<EventAttendee[]>([])

    const attendeesStateIds = useMemo(() => {
      return attendeesState.reduce<string[]>((acc, att) => {
        if (att?.user) {
          return [...acc, att.user.id]
        }
        return acc
      }, [])
    }, [attendeesState])

    const { user: currentUser } = useCurrentUser()

    const handleOpenChange = (open: boolean) => {
      if (open && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus()
        }, 0)
      }
    }

    const { data, isLoading, isFetching } = useGetApiV1SearchQuery(
      {
        q: debouncedSearchText,
        filter: ['users'],
      },
      {
        skip: !debouncedSearchText,
      },
    )
    const loading = isLoading || isFetching
    const options = data?.users || []

    const { data: attendees, isLoading: isLoadingAttendees } =
      useGetApiV1EventsByEventIdAttendeesQuery(
        { eventId: eventId || '' },
        {
          skip: !readOnly && (!eventId || !hasAttendees),
        },
      )

    const loadingAttendees = isLoadingAttendees

    const [
      addAttendeeRequest,
      { isLoading: isLoadingAdd, originalArgs: addAttendeeArgs },
    ] = usePostApiV1EventsByEventIdAttendeesAndUserIdMutation()

    const [
      deleteAttendeeRequest,
      { isLoading: isLoadingDelete, originalArgs: deleteAttendeeArgs },
    ] = useDeleteApiV1EventsByEventIdAttendeesAndUserIdMutation()

    const loadingAttendeeId = useMemo(() => {
      if (isLoadingAdd) {
        return addAttendeeArgs?.userId
      }
      if (isLoadingDelete) {
        return deleteAttendeeArgs?.userId
      }
      return undefined
    }, [
      isLoadingAdd,
      isLoadingDelete,
      addAttendeeArgs?.userId,
      deleteAttendeeArgs?.userId,
    ])

    // Sync attendees state when API fetches
    useEffect(() => {
      updateAttendees()
    }, [attendees])

    function updateAttendees() {
      if (attendees) {
        setAttendeesState(attendees)

        // Only for (create|copy)
        if (onChange) {
          const ids = attendees
            .filter((att) => !!att.user)
            .map((att: EventResolvedAttendee) => att.user.id)

          onChange(ids)
        }
      }
    }

    const handleSelect = async (user: User) => {
      const exists = attendeesStateIds.includes(user.id)

      if (isEdit) {
        if (exists) {
          await deleteAttendeeRequest({
            eventId: eventId as string,
            userId: user.id,
          })
        } else {
          void addAttendeeRequest({
            eventId: eventId as string,
            userId: user.id,
          })
        }
      }

      const nextValues: EventAttendee[] = exists
        ? attendeesState.filter((att) => att?.user?.id !== user.id)
        : [
            ...attendeesState,
            {
              uri: user.uri,
              user: user,
              status: 'INVITED',
            },
          ]

      setAttendeesState(nextValues)

      // Event creation
      if (!isEdit) {
        const ids = (nextValues as EventResolvedAttendee[]).map(
          (att) => att.user.id,
        )
        onChange?.(ids)
      }
    }

    if (readOnly) {
      return (
        <div
          className={cn(
            'flex items-center justify-between',
            listClasses,
            className,
          )}
          data-testid="attendees-field-readonly"
        >
          <AttendeesList
            attendees={attendeesState}
            loading={loadingAttendees}
            disabled
          />
        </div>
      )
    }

    return (
      <FormSelect
        {...rest}
        onOpenChange={handleOpenChange}
        data-testid="attendees-field-select"
      >
        <FormSelectTrigger
          ref={ref}
          className={cn(listClasses, className)}
          data-testid="attendees-field-trigger"
        >
          <AttendeesList
            attendees={attendeesState}
            loading={loadingAttendees}
            disabled={rest?.disabled}
            onRemove={(user) => void handleSelect(user)}
            loadingAttendeeId={loadingAttendeeId}
            hideStatusIcons={!isEdit}
            className="p-xs"
          />
        </FormSelectTrigger>
        <FormSelectContent className="h-[350px] p-none">
          <SelectActions className="px-none">
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                data-testid="attendees-search-input"
              />
            </div>
          </SelectActions>
          <FormSelectGroup className="flex-1">
            {loading ? (
              <div
                className="flex h-full items-center justify-center"
                data-testid="attendees-loading"
              >
                <Loader2 size={20} className="animate-spin" />
              </div>
            ) : (
              <>
                {!options?.length && searchText && (
                  <div
                    className="flex h-full items-center justify-center"
                    data-testid="no-users-found"
                  >
                    <span className="text-sm">No users found</span>
                  </div>
                )}
                {options.map((option) => {
                  // Do not show current user option
                  if (option.id === currentUser?.id) {
                    return null
                  }

                  const isSelected = attendeesStateIds.includes(option.id)
                  return (
                    <SelectCheckboxItem
                      key={option.id}
                      value={option.id}
                      isSelected={isSelected}
                      isLoading={option.id === loadingAttendeeId}
                      onSelect={() => void handleSelect(option)}
                      className={cn({
                        'pointer-events-none': loadingAttendeeId,
                      })}
                      data-testid={`attendee-option-${option.id}`}
                    >
                      <EventUser user={option} />
                    </SelectCheckboxItem>
                  )
                })}
              </>
            )}
          </FormSelectGroup>
          <SelectFooter className="flex justify-end">
            <span
              className="text-sm text-gray-600"
              data-testid="attendees-count"
            >
              {attendeesState.length} selected
            </span>
          </SelectFooter>
        </FormSelectContent>
      </FormSelect>
    )
  },
)

AttendeesField.displayName = 'AttendeesField'
